import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, Row, Col, Table, Tabs, Tab, Pagination } from "react-bootstrap";
import Typography from "@mui/material/Typography";
import { createChart, ColorType, LineSeries } from "lightweight-charts";
import axios from "axios";
import {
  TradingInstance,
  Algo,
  Symbol,
  AssetClass,
  Trade,
  Notification,
  UTMLPosition,
} from "../types";
import { CheckCircle, PauseCircle, AlertCircle, Timer, ChevronUp, ChevronDown, ArrowUpDown, LineChart } from "lucide-react";
import { getCSRFToken } from "../utils/csrf";
import StrategyChart from "../components/charts/StrategyChart";

const InstanceDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const seriesRef = useRef<any>(null);

  const [instance, setInstance] = useState<TradingInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allAssetClasses, setAllAssetClasses] = useState<AssetClass[]>([]);
  const [allBrokers, setAllBrokers] = useState<string[]>([]);
  const [lastUpdate, setLastUpdate] = useState<string>("");
  const [trades, setTrades] = useState<Trade[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [openPositions, setOpenPositions] = useState<UTMLPosition[]>([]);
  const [openPositionsLoading, setOpenPositionsLoading] = useState(false);

  // Pagination and Sorting states for Open Positions
  const [pagePositions, setPagePositions] = useState(1);
  const [perPagePositions] = useState(50);
  const [totalPositions, setTotalPositions] = useState(0);
  const [sortByPositions, setSortByPositions] = useState("entry_time");
  const [sortOrderPositions, setSortOrderPositions] = useState<"asc" | "desc">("desc");

  // Pagination and Sorting states for Trade History
  const [pageTrades, setPageTrades] = useState(1);
  const [perPageTrades] = useState(50);
  const [totalTrades, setTotalTrades] = useState(0);
  const [sortByTrades, setSortByTrades] = useState("date");
  const [sortOrderTrades, setSortOrderTrades] = useState<"asc" | "desc">("desc");

  // Pending Positions State
  const [pendingPositions, setPendingPositions] = useState<any[]>([]);
  const [pendingPositionsLoading, setPendingPositionsLoading] = useState(false);
  const [pagePending, setPagePending] = useState(1);
  const [perPagePending, setPerPagePending] = useState(50);
  const [totalPending, setTotalPending] = useState(0);

  // Strategy Visualization State
  const [ohlcMap, setOhlcMap] = useState<any>(null);
  const [ohlcLoading, setOhlcLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState<string>("");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Initialize chart
  useEffect(() => {
    console.log('🎨 [CHART INIT] Chart initialization effect triggered');
    console.log('🎨 [CHART INIT] Container ref:', chartContainerRef.current);
    console.log('🎨 [CHART INIT] Chart ref:', chartRef.current);

    const initChart = () => {
      if (chartContainerRef.current && !chartRef.current) {
        const container = chartContainerRef.current;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        console.log('🎨 [CHART INIT] Creating chart...');
        console.log('🎨 [CHART INIT] Container dimensions:', {
          width: containerWidth,
          height: containerHeight,
          offsetWidth: container.offsetWidth,
          offsetHeight: container.offsetHeight
        });

        // Check if container has valid dimensions
        if (containerWidth === 0 || containerHeight === 0) {
          console.warn('⚠️ [CHART INIT] Container has zero dimensions, cannot create chart', {
            clientWidth: containerWidth,
            clientHeight: containerHeight,
            offsetWidth: container.offsetWidth,
            offsetHeight: container.offsetHeight,
            computedStyle: window.getComputedStyle(container).display
          });
          return;
        }

        // Ensure minimum dimensions
        const chartWidth = Math.max(containerWidth, 100);
        const chartHeight = Math.max(containerHeight, 100);

        console.log('🎨 [CHART INIT] Creating chart with dimensions:', {
          width: chartWidth,
          height: chartHeight
        });

        try {
          const chart = createChart(container, {
            width: chartWidth,
            height: chartHeight,
            layout: {
              background: { color: "transparent" },
              textColor: "#8b919a",
            },
            grid: {
              vertLines: { color: "#1a1e2a" },
              horzLines: { color: "#1a1e2a" },
            },
            crosshair: {
              mode: 1,
              vertLine: {
                color: "#2196f3",
                width: 1,
                style: 1,
              },
              horzLine: {
                color: "#2196f3",
                width: 1,
                style: 1,
              },
            },
            rightPriceScale: {
              borderColor: "#ccc",
              scaleMargins: {
                top: 0.1,
                bottom: 0.1,
              },
            },
            timeScale: {
              borderColor: "#ccc",
              timeVisible: true,
              secondsVisible: false,
              rightOffset: 12,
              barSpacing: 3,
            },
          });

          // Add line series using LineSeries definition (correct API for v5)
          console.log('🎨 [CHART INIT] Adding line series with LineSeries definition...');

          const lineSeriesOptions = {
            color: "#2196f3",
            lineWidth: 2,
            crosshairMarkerVisible: true,
            lastValueVisible: true,
            priceLineVisible: false,
          };

          const lineSeries = chart.addSeries(LineSeries, lineSeriesOptions);
          console.log('✅ [CHART INIT] Line series created:', lineSeries);

          chartRef.current = chart;
          seriesRef.current = lineSeries;
          console.log('✅ [CHART INIT] Chart created successfully');
          console.log('✅ [CHART INIT] Series ref set:', seriesRef.current);
          console.log('✅ [CHART INIT] Chart ref set:', chartRef.current);

          // If we already have trades and series was created, set the data immediately
          if (lineSeries && trades && trades.length > 0) {
            console.log('🎨 [CHART INIT] Trades already available, setting initial data');
            // Trigger data update
            updateChartData();
          }
          else if (!lineSeries) {
            console.warn('⚠️ [CHART INIT] No series created, cannot set initial data');
          }
        } catch (error) {
          console.error('❌ [CHART INIT] Error creating chart:', error);
        }
      } else {
        if (chartRef.current) {
          console.log('⚠️ [CHART INIT] Chart already exists');
        } else {
          console.log('⚠️ [CHART INIT] Container not available yet');
        }
      }
    };

    // Try immediate initialization
    if (chartContainerRef.current) {
      const container = chartContainerRef.current;
      const hasDimensions = container.clientWidth > 0 && container.clientHeight > 0;
      console.log('🎨 [CHART INIT] Immediate init check:', {
        hasContainer: !!container,
        hasDimensions,
        width: container.clientWidth,
        height: container.clientHeight
      });

      if (hasDimensions) {
        initChart();
      } else {
        console.log('🎨 [CHART INIT] Container has no dimensions yet, will retry');
      }
    }

    // Also try after delays in case container isn't ready
    const timeoutId1 = setTimeout(() => {
      console.log('🎨 [CHART INIT] Retry initialization after 100ms');
      if (chartContainerRef.current) {
        const container = chartContainerRef.current;
        console.log('🎨 [CHART INIT] Retry 1 - Container dimensions:', {
          width: container.clientWidth,
          height: container.clientHeight
        });
      }
      initChart();
    }, 100);

    const timeoutId2 = setTimeout(() => {
      console.log('🎨 [CHART INIT] Retry initialization after 500ms');
      if (chartContainerRef.current) {
        const container = chartContainerRef.current;
        console.log('🎨 [CHART INIT] Retry 2 - Container dimensions:', {
          width: container.clientWidth,
          height: container.clientHeight
        });
      }
      initChart();
    }, 500);

    const timeoutId3 = setTimeout(() => {
      console.log('🎨 [CHART INIT] Retry initialization after 1000ms');
      if (chartContainerRef.current) {
        const container = chartContainerRef.current;
        console.log('🎨 [CHART INIT] Retry 3 - Container dimensions:', {
          width: container.clientWidth,
          height: container.clientHeight
        });
      }
      initChart();
    }, 1000);

    const handleResize = () => {
      if (chartRef.current && chartContainerRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      window.removeEventListener("resize", handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        seriesRef.current = null;
      }
    };
  }, [trades, instance]); // Add trades and instance as dependency to re-initialize if needed

  // Update chart data when trades change
  useEffect(() => {
    console.log('📊 [CHART] Chart data update triggered');
    console.log('📊 [CHART] Trades received:', trades);
    console.log('📊 [CHART] Series ref:', seriesRef.current);
    console.log('📊 [CHART] Chart ref:', chartRef.current);

    const updateChartData = () => {
      if (seriesRef.current && chartRef.current && instance) {
        // Sort trades by date in ascending order (oldest first) for cumulative P&L calculation
        const sortedTrades = trades
          ? trades
            .filter((trade) => trade.date) // Filter out trades without dates
            .sort(
              (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
            )
          : [];

        console.log('📊 [CHART] Sorted trades (after filtering):', sortedTrades);
        console.log('📊 [CHART] Number of trades with dates:', sortedTrades.length);

        // Prepare chart data starting with 0 PnL at instance creation
        const chartData: { time: any; value: number }[] = [];

        if (instance.created_at) {
          const creationTime = Math.floor(new Date(instance.created_at).getTime() / 1000);
          chartData.push({
            time: creationTime as any,
            value: 0,
          });
          console.log('📊 [CHART] Added initial data point at creation:', instance.created_at);
        }

        if (sortedTrades.length > 0) {
          // Calculate cumulative P&L using net_pnl (from UTML positions) if available, otherwise use pnl
          let cumulativePnL = 0;
          const tradeDataMap = new Map<number, number>();

          sortedTrades.forEach((trade) => {
            // Use net_pnl if available (from UTML positions), otherwise fall back to pnl
            const tradePnL = trade.net_pnl !== undefined ? trade.net_pnl : trade.pnl;
            cumulativePnL += Number(tradePnL);

            // Use Unix timestamp in seconds for lightweight-charts
            const timeValue = Math.floor(new Date(trade.date).getTime() / 1000);

            // Aggregate trades in the same second by always taking the latest cumulative PnL
            tradeDataMap.set(timeValue, cumulativePnL);
          });

          // Add aggregated trade data to chartData
          tradeDataMap.forEach((value, time) => {
            // If creation time is the same as a trade time, the trade cumulative PnL wins
            const existingIndex = chartData.findIndex(d => (d.time as number) === time);
            if (existingIndex !== -1) {
              chartData[existingIndex].value = value;
            } else {
              chartData.push({ time: time as any, value });
            }
          });

          // Final sort by time to ensure lightweight-charts requirement
          chartData.sort((a, b) => (a.time as number) - (b.time as number));

          console.log('📊 [CHART] Chart data generated:', chartData);
          console.log('📊 [CHART] First data point:', chartData[0]);
          console.log('📊 [CHART] Last data point:', chartData[chartData.length - 1]);
          console.log('📊 [CHART] Total cumulative P&L:', cumulativePnL);

          try {
            seriesRef.current.setData(chartData);
            console.log('✅ [CHART] Data set successfully on series');

            // Fit content so all data is visible
            chartRef.current.timeScale().fitContent();

            // Resize chart to ensure it displays properly
            if (chartContainerRef.current && chartRef.current) {
              chartRef.current.applyOptions({
                width: chartContainerRef.current.clientWidth,
              });
              console.log('✅ [CHART] Chart resized, width:', chartContainerRef.current.clientWidth);
            }
            return true; // Success
          } catch (error) {
            console.error('❌ [CHART] Error setting chart data:', error);
            return false;
          }
        } else {
          console.log('⚠️ [CHART] No trades with valid dates, clearing chart');
          // Clear chart if no trades
          try {
            seriesRef.current.setData([]);
            return true;
          } catch (error) {
            console.error('❌ [CHART] Error clearing chart data:', error);
            return false;
          }
        }
      } else {
        console.warn('⚠️ [CHART] Chart or series ref not available:', {
          hasSeriesRef: !!seriesRef.current,
          hasChartRef: !!chartRef.current
        });
        return false; // Not ready yet
      }
    };

    // Try to update immediately
    if (updateChartData()) {
      return; // Success, no need to retry
    }

    // If chart not ready, retry after delays
    if (trades && trades.length > 0) {
      console.log('📊 [CHART] Chart not ready, will retry setting data...');
      const retryTimeout1 = setTimeout(() => {
        console.log('📊 [CHART] Retry 1: Attempting to set data...');
        if (updateChartData()) {
          console.log('✅ [CHART] Retry 1: Success!');
        }
      }, 200);

      const retryTimeout2 = setTimeout(() => {
        console.log('📊 [CHART] Retry 2: Attempting to set data...');
        if (updateChartData()) {
          console.log('✅ [CHART] Retry 2: Success!');
        }
      }, 1000);

      return () => {
        clearTimeout(retryTimeout1);
        clearTimeout(retryTimeout2);
      };
    }
  }, [trades, instance]);

  useEffect(() => {
    setLoading(true);
    fetchInstance();
    fetchAllAssetClasses();
    // fetchAllBrokers();
  }, []);

  const fetchInstance = async (isUpdate = false) => {
    try {
      if (!isUpdate) {
        setLoading(true);
      } else {
        setUpdating(true);
      }
      setError(null);
      const response = await axios.get(
        `${BACKEND_URL}/api/trading-instances/${id}`
      );
      console.log("Instance data retrieved:", response.data);
      setInstance(response.data);
      setLastUpdate(new Date().toLocaleTimeString());
    } catch (err: any) {
      setError(
        err.response?.data?.detail || "Failed to fetch instance details"
      );
    } finally {
      if (!isUpdate) {
        setLoading(false);
      } else {
        setUpdating(false);
      }
    }
  };

  const fetchAllAssetClasses = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/trading-instances/${id}/asset-classes`
      );
      setAllAssetClasses(response.data);
    } catch (err) {
      console.error("Error fetching asset classes:", err);
    }
  };

  const fetchAllBrokers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/brokers`);
      console.log(response.data);
      setAllBrokers(response.data.map((broker: any) => broker.name));
    } catch (err) {
      console.error("Error fetching brokers:", err);
    }
  };

  const fetchTrades = async (isUpdate = false) => {
    try {
      if (!isUpdate) {
        setTradesLoading(true);
      }
      console.log('📥 [FETCH] Fetching trades from:', `${BACKEND_URL}/api/trading-instances/${id}/trades`);
      const response = await axios.get(
        `${BACKEND_URL}/api/trading-instances/${id}/trades`,
        {
          params: {
            page: pageTrades,
            per_page: perPageTrades,
            sort_by: sortByTrades,
            sort_order: sortOrderTrades
          }
        }
      );
      console.log('📥 [FETCH] Trades response:', response.data);
      setTrades(response.data.trades);
      setTotalTrades(response.data.total);
    } catch (err) {
      console.error("❌ [FETCH] Error fetching trades:", err);
    } finally {
      if (!isUpdate) {
        setTradesLoading(false);
      }
    }
  };

  const fetchOhlcData = async () => {
    try {
      setOhlcLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/trading-instances/${id}/ohlc-data`);
      setOhlcMap(response.data);
      if (response.data && Object.keys(response.data).length > 0 && !selectedSession) {
        setSelectedSession(Object.keys(response.data)[0]);
      }
    } catch (err) {
      console.error("Error fetching OHLC data:", err);
    } finally {
      setOhlcLoading(false);
    }
  };

  const fetchNotifications = async (isUpdate = false) => {
    try {
      if (!isUpdate) {
        setNotificationsLoading(true);
      }
      const response = await axios.get(
        `${BACKEND_URL}/api/trading-instances/${id}/notifications`
      );
      setNotifications(response.data.notifications);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      if (!isUpdate) {
        setNotificationsLoading(false);
      }
    }
  };

  const fetchOpenPositions = async (isUpdate = false) => {
    try {
      if (!isUpdate) setOpenPositionsLoading(true);

      const params = new URLSearchParams();
      params.append('page', pagePositions.toString());
      params.append('per_page', perPagePositions.toString());
      params.append('sort_by', sortByPositions);
      params.append('sort_order', sortOrderPositions);
      params.append('status', 'ACTIVE');
      params.append('status', 'CLOSING');

      const response = await axios.get(
        `${BACKEND_URL}/api/trading-instances/${id}/positions?${params.toString()}`
      );
      setOpenPositions(response.data.positions);
      setTotalPositions(response.data.total);
    } catch (err) {
      console.error("Error fetching open positions:", err);
    } finally {
      if (!isUpdate) setOpenPositionsLoading(false);
    }
  };

  const fetchPendingPositions = async (isUpdate = false) => {
    try {
      if (!isUpdate) setPendingPositionsLoading(true);

      const params = new URLSearchParams();
      params.append('page', pagePending.toString());
      params.append('per_page', perPagePending.toString());
      params.append('sort_by', 'entry_time');
      params.append('sort_order', 'desc');
      params.append('status', 'OPENING');

      const response = await axios.get(
        `${BACKEND_URL}/api/trading-instances/${id}/positions?${params.toString()}`
      );
      setPendingPositions(response.data.positions);
      setTotalPending(response.data.total);
    } catch (err) {
      console.error("Error fetching pending positions:", err);
    } finally {
      setPendingPositionsLoading(false);
    }
  };

  useEffect(() => {
    fetchInstance();
    fetchTrades();
    fetchNotifications();
    fetchOpenPositions();
    fetchPendingPositions();
    fetchOhlcData();
  }, [id, BACKEND_URL, pagePositions, perPagePositions, sortByPositions, sortOrderPositions, pageTrades, sortByTrades, sortOrderTrades, pagePending]);

  // Add keyboard shortcut for refresh (F5 key)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "F5") {
        event.preventDefault(); // Prevent browser refresh
        if (!updating && instance) {
          handleRefresh();
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [updating, instance]); // Dependencies for the effect

  const handleRefresh = async () => {
    if (!instance) return;

    try {
      setUpdating(true);
      await Promise.all([
        fetchInstance(true),
        fetchTrades(true),
        fetchNotifications(true),
        fetchOpenPositions(true),
        fetchPendingPositions(true),
      ]);
    } catch (error) {
      console.error("Failed to refresh instance data:", error);
      setError("Failed to refresh instance data");
    } finally {
      setUpdating(false);
    }
  };

  const handlePauseInstance = async () => {
    try {
      setUpdating(true);
      const csrfToken = await getCSRFToken();
      await axios.put(
        `${BACKEND_URL}/api/trading-instances/${id}`,
        {
          status: "paused",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );
      await fetchInstance(true);
    } catch (error) {
      console.error("Error pausing instance:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleResumeInstance = async () => {
    try {
      setUpdating(true);
      const csrfToken = await getCSRFToken();
      await axios.put(
        `${BACKEND_URL}/api/trading-instances/${id}`,
        {
          status: "live",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );
      await fetchInstance(true);
    } catch (error) {
      console.error("Error resuming instance:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteInstance = async () => {
    if (window.confirm("Are you sure you want to delete this instance?")) {
      try {
        const csrfToken = await getCSRFToken();
        await axios.delete(`${BACKEND_URL}/api/trading-instances/${id}`, {
          headers: {
            "X-CSRFToken": csrfToken,
          },
        });
        navigate("/live-trading");
      } catch (error) {
        console.error("Error deleting instance:", error);
      }
    }
  };

  // Helper to format exit reason
  const formatExitReason = (reason: string | null) => {
    if (!reason) return "-";
    const mapped: Record<string, string> = {
      STOP_LOSS: "SL",
      TAKE_PROFIT: "TP",
      MANUAL: "Manual",
      DRAWDOWN: "Drawdown",
      TIMEOUT: "Timeout",
      SIGNAL_EXIT: "Signal",
    };
    return mapped[reason] || reason;
  };

  const SortIcon = ({ currentSort, field, sortOrder }: { currentSort: string, field: string, sortOrder: "asc" | "desc" }) => {
    if (currentSort !== field) return <ArrowUpDown size={14} className="ms-1 text-muted" />;
    return sortOrder === "asc" ? <ChevronUp size={14} className="ms-1" /> : <ChevronDown size={14} className="ms-1" />;
  };

  const handleSortPositions = (field: string) => {
    if (sortByPositions === field) {
      setSortOrderPositions(sortOrderPositions === "asc" ? "desc" : "asc");
    } else {
      setSortByPositions(field);
      setSortOrderPositions("asc");
    }
  };

  const handleSortTrades = (field: string) => {
    if (sortByTrades === field) {
      setSortOrderTrades(sortOrderTrades === "asc" ? "desc" : "asc");
    } else {
      setSortByTrades(field);
      setSortOrderTrades("asc");
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!instance) return <div>Instance not found</div>;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "live":
        return <CheckCircle color="#2196f3" />;
      case "paused":
        return <PauseCircle color="#ff9800" />;
      case "interrupted":
        return <AlertCircle color="#f44336" />;
      case "pending":
        return <Timer color="#757575" />;
      default:
        return null;
    }
  };

  // Sample data for the chart - replace with actual data from your backend
  const performanceData = [
    { time: "09:30", balance: 10000, pnl: 0 },
    { time: "10:00", balance: 10050, pnl: 50 },
    { time: "10:30", balance: 10120, pnl: 120 },
    { time: "11:00", balance: 10080, pnl: 80 },
    // Add more data points...
  ];

  const calculatePerformanceMetrics = (trades: Trade[]) => {
    if (!trades || trades.length === 0) {
      return {
        totalTrades: 0,
        winningTrades: 0,
        losingTrades: 0,
        winRate: 0,
        avgPnL: 0,
        avgWinningTrade: 0,
        avgLosingTrade: 0,
        riskRewardRatio: 0,
        longTrades: 0,
        shortTrades: 0,
        winningLongTrades: 0,
        winningShortTrades: 0,
        losingLongTrades: 0,
        losingShortTrades: 0,
        longWinRate: 0,
        shortWinRate: 0,
        avgLongPnL: 0,
        avgShortPnL: 0,
        avgWinningLongTrade: 0,
        avgWinningShortTrade: 0,
        avgLosingLongTrade: 0,
        avgLosingShortTrade: 0,
        longRiskRewardRatio: 0,
        shortRiskRewardRatio: 0,
      };
    }

    // Helper function to get P&L value (prefer net_pnl from UTML positions)
    const getTradePnL = (trade: Trade): number => {
      return trade.net_pnl !== undefined ? trade.net_pnl : trade.pnl;
    };

    const longTrades = trades.filter((t) => t.side === "BUY");
    const shortTrades = trades.filter((t) => t.side === "SELL");
    const winningTrades = trades.filter((t) => getTradePnL(t) > 0);
    const losingTrades = trades.filter((t) => getTradePnL(t) <= 0);
    const winningLongTrades = longTrades.filter((t) => getTradePnL(t) > 0);
    const winningShortTrades = shortTrades.filter((t) => getTradePnL(t) > 0);
    const losingLongTrades = longTrades.filter((t) => getTradePnL(t) <= 0);
    const losingShortTrades = shortTrades.filter((t) => getTradePnL(t) <= 0);

    const calculateAvgPnL = (trades: Trade[]) =>
      trades.length > 0
        ? trades.reduce((sum, t) => sum + getTradePnL(t), 0) / trades.length
        : 0;

    const calculateWinRate = (winning: number, total: number) =>
      total > 0 ? (winning / total) * 100 : 0;

    const calculateRiskRewardRatio = (winning: Trade[], losing: Trade[]) => {
      const avgWin = calculateAvgPnL(winning);
      const avgLoss = Math.abs(calculateAvgPnL(losing));
      return avgLoss !== 0 ? Math.abs(avgWin / avgLoss) : 0;
    };

    return {
      totalTrades: trades.length,
      winningTrades: winningTrades.length,
      losingTrades: losingTrades.length,
      winRate: calculateWinRate(winningTrades.length, trades.length),
      avgPnL: calculateAvgPnL(trades),
      avgWinningTrade: calculateAvgPnL(winningTrades),
      avgLosingTrade: calculateAvgPnL(losingTrades),
      riskRewardRatio: calculateRiskRewardRatio(winningTrades, losingTrades),
      longTrades: longTrades.length,
      shortTrades: shortTrades.length,
      winningLongTrades: winningLongTrades.length,
      winningShortTrades: winningShortTrades.length,
      losingLongTrades: losingLongTrades.length,
      losingShortTrades: losingShortTrades.length,
      longWinRate: calculateWinRate(
        winningLongTrades.length,
        longTrades.length
      ),
      shortWinRate: calculateWinRate(
        winningShortTrades.length,
        shortTrades.length
      ),
      avgLongPnL: calculateAvgPnL(longTrades),
      avgShortPnL: calculateAvgPnL(shortTrades),
      avgWinningLongTrade: calculateAvgPnL(winningLongTrades),
      avgWinningShortTrade: calculateAvgPnL(winningShortTrades),
      avgLosingLongTrade: calculateAvgPnL(losingLongTrades),
      avgLosingShortTrade: calculateAvgPnL(losingShortTrades),
      longRiskRewardRatio: calculateRiskRewardRatio(
        winningLongTrades,
        losingLongTrades
      ),
      shortRiskRewardRatio: calculateRiskRewardRatio(
        winningShortTrades,
        losingShortTrades
      ),
    };
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <Typography variant="h4">{instance.name}</Typography>
          {getStatusIcon(instance.status)}
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            onClick={handleRefresh}
            disabled={updating}
            className="d-flex align-items-center"
            title="Refresh instance data (F5)"
          >
            <svg
              width="16"
              height="16"
              fill="currentColor"
              className={`me-1 ${updating ? "spinner-border spinner-border-sm" : ""
                }`}
              viewBox="0 0 16 16"
            >
              <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z" />
              <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
            </svg>
            {updating ? "Refreshing..." : "Refresh"}
          </Button>
          {instance.status === "live" ? (
            <Button
              variant="warning"
              onClick={handlePauseInstance}
              disabled={updating}
            >
              Pause Instance
            </Button>
          ) : (
            <Button
              variant="success"
              onClick={handleResumeInstance}
              disabled={updating}
            >
              Resume Instance
            </Button>
          )}
          <Button
            variant="danger"
            onClick={handleDeleteInstance}
            disabled={updating}
          >
            Delete Instance
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => navigate("/live-trading")}
          >
            Back to Live Trading
          </Button>
        </div>
      </div>

      {/* Subtle loading indicator for updates */}
      {updating && (
        <div
          className="alert alert-info alert-dismissible fade show"
          role="alert"
        >
          <div className="d-flex align-items-center">
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            Updating instance data...
          </div>
        </div>
      )}

      {/* Last update indicator */}
      {lastUpdate && (
        <div className="text-muted mb-3">
          <small>Last updated: {lastUpdate}</small>
        </div>
      )}

      {/* Broker Information Card */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <Typography variant="h5" className="mb-3">
                Brokerage Information
              </Typography>
              <div className="d-flex gap-4">
                <div>
                  <Typography>
                    {(instance.broker_account as any)?.broker_name ||
                      (instance.broker_account as any)?.name ||
                      "N/A"}
                  </Typography>
                </div>
                <div>
                  <Typography>
                    {(instance.broker_account as any)?.account_number ||
                      (instance.broker_account as any)?.account_id ||
                      "N/A"}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="mt-3">
        <Typography variant="body2" color="textSecondary">
          Symbols
        </Typography>
        <Typography>
          {instance.symbols?.map((symbol: Symbol) => symbol.name).join(" | ")}
        </Typography>
      </div>

      <div>
        <Card>
          <Card.Body>
            <Typography variant="h6" className="mb-3">
              Trading Permissions:
            </Typography>
            <div className="d-flex flex-wrap gap-2">
              {allAssetClasses?.map((assetClass: AssetClass) => (
                <div
                  key={assetClass.id}
                  className="d-flex align-items-center gap-1 p-2 border rounded"
                >
                  <CheckCircle
                    size={16}
                    color={assetClass.tradable === true ? "#4caf50" : "#757575"}
                  />
                  {assetClass.name}
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="mt-4">
        <Card>
          <Card.Body>
            <Typography variant="h6" className="mb-3">
              Account Overview
            </Typography>
            <div className="d-flex justify-content-between">
              <div>
                <Typography variant="body2" color="textSecondary">
                  Account Balance
                </Typography>
                <Typography variant="h6">
                  ${Number(instance.balance || 0).toFixed(2)}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">
                  Trading Capital
                </Typography>
                <Typography variant="h6">
                  ${Number(instance.cash_at_risk || 0).toFixed(2)}
                </Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">
                  All Time Profit
                </Typography>
                <Typography
                  variant="h6"
                  style={{
                    color: Number(instance.profit) >= 0 ? "#4caf50" : "#f44336",
                  }}
                >
                  {Number((instance.profit / instance.initial_balance) * 100) >=
                    0
                    ? "+"
                    : ""}
                  {Number(
                    (instance.profit / instance.initial_balance) * 100
                  ).toFixed(2)}
                  % ({instance.profit >= 0 ? "$" : "-$"}
                  {Math.abs(Number(instance.profit || 0)).toFixed(2)})
                </Typography>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Performance Chart */}
      <Card className="mt-4">
        <Card.Body>
          <Typography variant="h6" className="mb-3">
            P&L History
          </Typography>
          <div style={{ height: "300px", minHeight: "300px", position: "relative", width: "100%" }}>
            {/* Chart container - always rendered so ref is available */}
            <div
              ref={chartContainerRef}
              style={{
                width: "100%",
                height: "300px",
                minHeight: "300px",
                position: "relative"
              }}
            />
            {/* Loading overlay */}
            {tradesLoading && (
              <div className="text-center" style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", position: "absolute", width: "100%", top: 0, left: 0, zIndex: 1, backgroundColor: "rgba(255, 255, 255, 0.9)" }}>
                <Typography>Loading trade data...</Typography>
              </div>
            )}
            {/* No trades message */}
            {!tradesLoading && (!trades || trades.length === 0) && (
              <div className="text-center" style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", position: "absolute", width: "100%", top: 0, left: 0, zIndex: 1 }}>
                <Typography color="textSecondary">No closed trades yet. P&L history will appear here once trades are closed.</Typography>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Performance & Trade History */}
      <Card className="mb-4">
        <Card.Body>
          <Tabs defaultActiveKey="open-positions" className="mb-3">
            <Tab eventKey="pending-positions" title={`Pending (${totalPending})`}>
              <Typography variant="h5" className="mb-4">
                Opening Positions
              </Typography>
              {pendingPositionsLoading ? (
                <div className="text-center">
                  <Typography>Loading pending positions...</Typography>
                </div>
              ) : pendingPositions && pendingPositions.length > 0 ? (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Typography variant="body2" className="text-muted">
                      Showing {pendingPositions.length} of {totalPending} pending positions
                    </Typography>
                    <Pagination size="sm" className="mb-0">
                      <Pagination.Prev
                        disabled={pagePending === 1}
                        onClick={() => setPagePending(p => Math.max(1, p - 1))}
                      />
                      <Pagination.Item active>{pagePending}</Pagination.Item>
                      <Pagination.Next
                        disabled={pendingPositions.length < perPagePending && pagePending * perPagePending >= totalPending}
                        onClick={() => setPagePending(p => p + 1)}
                      />
                    </Pagination>
                  </div>
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <Table responsive hover size="sm">
                      <thead style={{ position: "sticky", top: 0, backgroundColor: "#181b24", zIndex: 1, boxShadow: "0 1px 0 rgba(0,0,0,0.05)" }}>
                        <tr>
                          <th>Position ID</th>
                          <th>Time</th>
                          <th>Symbol</th>
                          <th>Side</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingPositions.map((pos) => (
                          <tr key={pos.id}>
                            <td>{pos.broker_position_id || pos.id}</td>
                            <td>{pos.entry_time ? new Date(pos.entry_time).toLocaleString() : "N/A"}</td>
                            <td>{pos.symbol}</td>
                            <td
                              style={{
                                color: pos.direction === "LONG" ? "#4caf50" : "#f44336",
                                fontWeight: "bold",
                              }}
                            >
                              {pos.direction}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : (
                <div className="text-center p-4">
                  <Typography color="textSecondary">No pending positions at the moment.</Typography>
                </div>
              )}
            </Tab>
            {/* <Tab eventKey="strategy" title={<span><LineChart size={18} className="me-2" />Strategy Visualization</span>}>
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Typography variant="h5">Strategy Visualizer</Typography>
                  <div className="d-flex gap-2">
                    {ohlcMap && Object.keys(ohlcMap).length > 1 && (
                      <select
                        className="form-select"
                        value={selectedSession}
                        onChange={(e) => setSelectedSession(e.target.value)}
                        style={{ width: 'auto' }}
                      >
                        {Object.keys(ohlcMap).map(key => (
                          <option key={key} value={key}>{key}</option>
                        ))}
                      </select>
                    )}
                    <Button
                      variant="outline-secondary"
                      onClick={fetchOhlcData}
                      disabled={ohlcLoading}
                      size="sm"
                    >
                      Refresh Data
                    </Button>
                  </div>
                </div>

                {ohlcLoading ? (
                  <div className="text-center p-5">
                    <Typography>Loading strategy data...</Typography>
                  </div>
                ) : ohlcMap && selectedSession && ohlcMap[selectedSession] ? (
                  <Card className="p-0 overflow-hidden" style={{ border: '1px solid #2a2e39', backgroundColor: '#131722' }}>
                    <StrategyChart
                      ohlc={ohlcMap[selectedSession].ohlc}
                      indicators={ohlcMap[selectedSession].indicators}
                      markers={ohlcMap[selectedSession].markers}
                      height={600}
                    />
                  </Card>
                ) : (
                  <div className="text-center p-5 rounded" style={{ background: "var(--gogi-surface-2)", border: "1px solid var(--gogi-border)" }}>
                    <Typography color="textSecondary">No data available for strategy visualization.</Typography>
                  </div>
                )}
              </div>
            </Tab> */}
            <Tab eventKey="open-positions" title="Open Positions">
              <Typography variant="h5" className="mb-4">
                Active UTML Positions
              </Typography>
              {openPositionsLoading ? (
                <div className="text-center">
                  <Typography>Loading open positions...</Typography>
                </div>
              ) : openPositions && openPositions.length > 0 ? (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Typography variant="body2" className="text-muted">
                      Showing {openPositions.length} of {totalPositions} active positions
                    </Typography>
                    <Pagination size="sm" className="mb-0">
                      <Pagination.Prev
                        disabled={pagePositions === 1}
                        onClick={() => setPagePositions(p => Math.max(1, p - 1))}
                      />
                      <Pagination.Item active>{pagePositions}</Pagination.Item>
                      <Pagination.Next
                        disabled={openPositions.length < perPagePositions && pagePositions * perPagePositions >= totalPositions}
                        onClick={() => setPagePositions(p => p + 1)}
                      />
                    </Pagination>
                  </div>
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <Table responsive hover size="sm">
                      <thead style={{ position: "sticky", top: 0, backgroundColor: "#181b24", zIndex: 1, boxShadow: "0 1px 0 rgba(0,0,0,0.05)" }}>
                        <tr>
                          <th>Position ID</th>
                          <th onClick={() => handleSortPositions("entry_time")} style={{ cursor: "pointer" }}>
                            Time <SortIcon currentSort={sortByPositions} field="entry_time" sortOrder={sortOrderPositions} />
                          </th>
                          <th onClick={() => handleSortPositions("symbol")} style={{ cursor: "pointer" }}>
                            Symbol <SortIcon currentSort={sortByPositions} field="symbol" sortOrder={sortOrderPositions} />
                          </th>
                          <th>Side</th>
                          <th>Quantity</th>
                          <th>Entry Price</th>
                          <th>Current Price</th>
                          <th>SL</th>
                          <th>TP</th>
                          <th>Unrealized P&L</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {openPositions.map((pos) => (
                          <tr key={pos.id}>
                            <td>{pos.broker_position_id || pos.id}</td>
                            <td>{pos.entry_time ? new Date(pos.entry_time).toLocaleString() : "N/A"}</td>
                            <td>{pos.symbol}</td>
                            <td
                              style={{
                                color: pos.direction === "LONG" ? "#4caf50" : "#f44336",
                                fontWeight: "bold",
                              }}
                            >
                              {pos.direction}
                            </td>
                            <td>{pos.quantity}</td>
                            <td>
                              {pos.entry_price !== null ? Number(pos.entry_price).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 8
                              }) : "N/A"}
                            </td>
                            <td>
                              {pos.current_price !== null ? Number(pos.current_price).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 8
                              }) : "N/A"}
                            </td>
                            <td>
                              {pos.stop_loss_price !== null ? Number(pos.stop_loss_price).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 8
                              }) : "N/A"}
                            </td>
                            <td>
                              {pos.take_profit_price !== null ? Number(pos.take_profit_price).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 8
                              }) : "N/A"}
                            </td>
                            <td
                              className={
                                pos.net_pnl !== null && pos.net_pnl !== undefined
                                  ? Number(pos.net_pnl) >= 0
                                    ? "text-success"
                                    : "text-danger"
                                  : ""
                              }
                              style={{ fontWeight: "bold" }}
                            >
                              {pos.net_pnl !== null && pos.net_pnl !== undefined
                                ? Number(pos.net_pnl).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })
                                : "N/A"}
                            </td>
                            <td>
                              <span
                                className={`badge ${pos.status === "ACTIVE"
                                  ? "bg-success"
                                  : pos.status === "OPENING"
                                    ? "bg-warning"
                                    : "bg-info"
                                  }`}
                              >
                                {pos.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : (
                <Typography className="text-muted">
                  No active positions
                </Typography>
              )}
            </Tab>
            <Tab eventKey="performance" title="Performance">
              <Typography variant="h5" className="mb-4">
                Performance Metrics
              </Typography>
              {tradesLoading ? (
                <div className="text-center">
                  <Typography>Loading performance metrics...</Typography>
                </div>
              ) : trades && trades.length > 0 ? (
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>All</th>
                      <th>Long</th>
                      <th>Short</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted">
                    {(() => {
                      const metrics = calculatePerformanceMetrics(trades);
                      return (
                        <>
                          <tr>
                            <td>Total trades</td>
                            <td>{metrics.totalTrades}</td>
                            <td>{metrics.longTrades}</td>
                            <td>{metrics.shortTrades}</td>
                          </tr>
                          <tr>
                            <td>Winning trades</td>
                            <td>{metrics.winningTrades}</td>
                            <td>{metrics.winningLongTrades}</td>
                            <td>{metrics.winningShortTrades}</td>
                          </tr>
                          <tr>
                            <td>Losing trades</td>
                            <td>{metrics.losingTrades}</td>
                            <td>{metrics.losingLongTrades}</td>
                            <td>{metrics.losingShortTrades}</td>
                          </tr>
                          <tr>
                            <td>Win rate</td>
                            <td>{metrics.winRate.toFixed(2)}%</td>
                            <td>{metrics.longWinRate.toFixed(2)}%</td>
                            <td>{metrics.shortWinRate.toFixed(2)}%</td>
                          </tr>
                          <tr>
                            <td>Avg P&L</td>
                            <td
                              className={
                                metrics.avgPnL >= 0
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              ${metrics.avgPnL.toFixed(2)}
                            </td>
                            <td
                              className={
                                metrics.avgLongPnL >= 0
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              ${metrics.avgLongPnL.toFixed(2)}
                            </td>
                            <td
                              className={
                                metrics.avgShortPnL >= 0
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              ${metrics.avgShortPnL.toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td>Avg winning trade</td>
                            <td>${metrics.avgWinningTrade.toFixed(2)}</td>
                            <td>${metrics.avgWinningLongTrade.toFixed(2)}</td>
                            <td>${metrics.avgWinningShortTrade.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>Avg losing trade</td>
                            <td>${metrics.avgLosingTrade.toFixed(2)}</td>
                            <td>${metrics.avgLosingLongTrade.toFixed(2)}</td>
                            <td>${metrics.avgLosingShortTrade.toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td>Risk/reward ratio</td>
                            <td>{metrics.riskRewardRatio.toFixed(3)}</td>
                            <td>{metrics.longRiskRewardRatio.toFixed(3)}</td>
                            <td>{metrics.shortRiskRewardRatio.toFixed(3)}</td>
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </Table>
              ) : (
                <Typography className="text-muted">
                  No trades available
                </Typography>
              )}
            </Tab>
            <Tab eventKey="trades" title="Trade History">
              <Typography variant="h5" className="mb-4">
                Recent Trades
              </Typography>
              {tradesLoading ? (
                <div className="text-center">
                  <Typography>Loading trades...</Typography>
                </div>
              ) : trades && trades.length > 0 ? (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Typography variant="body2" className="text-muted">
                      Showing {trades.length} of {totalTrades} records
                    </Typography>
                    <Pagination size="sm" className="mb-0">
                      <Pagination.Prev
                        disabled={pageTrades === 1}
                        onClick={() => setPageTrades(p => Math.max(1, p - 1))}
                      />
                      <Pagination.Item active>{pageTrades}</Pagination.Item>
                      <Pagination.Next
                        disabled={trades.length < perPageTrades && pageTrades * perPageTrades >= totalTrades}
                        onClick={() => setPageTrades(p => p + 1)}
                      />
                    </Pagination>
                  </div>
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <Table responsive hover size="sm">
                      <thead style={{ position: "sticky", top: 0, backgroundColor: "#181b24", zIndex: 1, boxShadow: "0 1px 0 rgba(0,0,0,0.05)" }}>
                        <tr>
                          <th>Position ID</th>
                          <th onClick={() => handleSortTrades("entry_time")} style={{ cursor: "pointer" }}>
                            Entry Time <SortIcon currentSort={sortByTrades} field="entry_time" sortOrder={sortOrderTrades} />
                          </th>
                          <th onClick={() => handleSortTrades("date")} style={{ cursor: "pointer" }}>
                            Exit Time <SortIcon currentSort={sortByTrades} field="date" sortOrder={sortOrderTrades} />
                          </th>
                          <th>Symbol</th>
                          <th>SL</th>
                          <th>TP</th>
                          <th>Side</th>
                          <th>Quantity</th>
                          <th>Entry Price</th>
                          <th>Exit Price</th>
                          <th>Exit Reason</th>
                          <th>P&L</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trades.map((trade) => (
                          <tr key={trade.id}>
                            <td>{trade.broker_position_id || trade.id}</td>
                            <td>{new Date(trade.entry_time || trade.date).toLocaleString()}</td>
                            <td>{trade.exit_time ? new Date(trade.exit_time).toLocaleString() : "N/A"}</td>
                            <td>{trade.symbol}</td>
                            <td>{trade.stop_loss_price?.toFixed(5) || "N/A"}</td>
                            <td>{trade.take_profit_price?.toFixed(5) || "N/A"}</td>
                            <td>{trade.side}</td>
                            <td>{trade.quantity}</td>
                            <td>
                              {Number(trade.entry_price).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 8
                              })}
                            </td>
                            <td>
                              {Number(trade.exit_price).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 8
                              })}
                            </td>
                            <td>
                              <span className={`badge bg-secondary`}>
                                {formatExitReason(trade.exit_reason)}
                              </span>
                            </td>
                            <td
                              className={
                                Number(trade.net_pnl !== undefined ? trade.net_pnl : trade.pnl) >= 0
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {Number(trade.net_pnl !== undefined ? trade.net_pnl : trade.pnl).toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : (
                <Typography className="text-muted">
                  No trades available
                </Typography>
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      {/* Notifications Card */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Typography variant="h5">Notifications</Typography>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => navigate(`/instance-notifications/${id}`)}
            >
              View All
            </Button>
          </div>
          {notificationsLoading ? (
            <div className="text-center">
              <Typography>Loading notifications...</Typography>
            </div>
          ) : notifications && notifications.length > 0 ? (
            <div
              className="notification-list"
              style={{ maxHeight: "300px", overflowY: "auto" }}
            >
              {notifications
                .sort(
                  (a, b) =>
                    new Date(b.timestamp).getTime() -
                    new Date(a.timestamp).getTime()
                )
                .map((notification) => (
                  <div key={notification.id} className="notification-item mb-3 p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <Typography variant="body2" color="textSecondary">
                        {new Date(notification.timestamp).toLocaleString()}
                      </Typography>
                      <div className="d-flex gap-1">
                        {notification.priority && (
                          <span className={`badge ${notification.priority === 'CRITICAL' ? 'bg-danger' :
                            notification.priority === 'HIGH' ? 'bg-warning' :
                              notification.priority === 'NORMAL' ? 'bg-info' : 'bg-secondary'
                            }`}>
                            {notification.priority}
                          </span>
                        )}
                        {notification.notification_type && (
                          <span className="badge bg-primary">
                            {notification.notification_type}
                          </span>
                        )}
                        {notification.linked_algo && (
                          <span className="badge bg-success">
                            {notification.linked_algo.name}
                          </span>
                        )}
                      </div>
                    </div>
                    {(() => {
                      const message = notification.message;

                      // Check if this is a UTML data fetch notification
                      if (message.includes("Data Fetch Success") && message.includes("📈 Latest Candle OHLC Data:")) {
                        return (
                          <div className="mb-2">
                            <div className="mb-2">
                              <span className="badge bg-primary me-1">UTML</span>
                              <span className="badge bg-success me-1">Data Fetch</span>
                              <strong>{message.split('\n')[0]}</strong>
                            </div>
                            <div className="small text-muted">
                              <pre className="gogi-code-block">
                                {message.split('\n').slice(1).join('\n')}
                              </pre>
                            </div>
                          </div>
                        );
                      }

                      // Check if this is a UTML notification with badges
                      const utmlMatch = message.match(/^📊 \[UTML\] \[([^\]]+)\] \[([^\]]+)\] (.+)/);
                      if (utmlMatch) {
                        const [, algorithmName, instanceName, restOfMessage] = utmlMatch;
                        return (
                          <div className="mb-2">
                            <div className="mb-1">
                              <span className="badge bg-primary me-1">UTML</span>
                              <span className="badge bg-info me-1">{algorithmName}</span>
                              <span className="badge bg-secondary me-1">{instanceName}</span>
                            </div>
                            <Typography variant="body2">
                              {restOfMessage}
                            </Typography>
                          </div>
                        );
                      }

                      // Regular notification
                      return (
                        <Typography className="mb-2">{notification.message}</Typography>
                      );
                    })()}
                    {notification.tags && notification.tags.length > 0 && (
                      <div className="d-flex flex-wrap gap-1">
                        {notification.tags.map((tag, index) => (
                          <span
                            key={index}
                            className={`badge ${tag.startsWith('instance-') ? 'bg-dark' :
                              tag.startsWith('utml') ? 'bg-primary' :
                                tag.startsWith('broker-') ? 'bg-info' :
                                  tag.startsWith('algo-') ? 'bg-success' :
                                    tag.startsWith('symbol-') ? 'bg-warning' :
                                      tag.includes('error') || tag.includes('failed') ? 'bg-danger' :
                                        tag.includes('success') ? 'bg-success' :
                                          tag.includes('warning') ? 'bg-warning' :
                                            'bg-secondary'
                              }`}
                            style={{ fontSize: '0.75rem' }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <Typography className="text-muted">
              No notifications available
            </Typography>
          )}
        </Card.Body>
      </Card>

      {/* Algos */}
      <Card className="mb-4">
        <Card.Body>
          <Typography variant="h5" className="mb-3">
            Algos
          </Typography>
          <Row>
            {instance?.linked_algos?.map((algo) => (
              <Col key={algo.id} md={6} lg={4} className="mb-3">
                <Card
                  className="algo-summary-card"
                  style={{
                    backgroundColor: "#1a1a1a",
                    borderRadius: "12px",
                    border: "1px solid #333",
                    fontFamily:
                      "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                  }}
                >
                  <Card.Body style={{ padding: "20px" }}>
                    {/* Header Section */}
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <Typography
                        variant="h6"
                        style={{
                          color: "#ffffff",
                          fontWeight: "bold",
                          fontSize: "16px",
                          margin: 0,
                        }}
                      >
                        {algo.name}
                      </Typography>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => navigate(`/algo-code/${algo.id}`)}
                        style={{
                          color: "#888888",
                          fontSize: "12px",
                          margin: 0,
                          padding: 0,
                          textDecoration: "none",
                          minWidth: "auto",
                        }}
                        title="View Algo Code"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          style={{ transform: "rotate(45deg)" }}
                        >
                          <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
                        </svg>
                      </Button>
                    </div>

                    {/* Middle Section */}
                    <div className="mb-3">
                      <Typography
                        variant="body2"
                        style={{
                          color: "#4fc3f7",
                          fontSize: "11px",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          marginBottom: "8px",
                        }}
                      >
                        {algo.parameters?.assetClasses
                          ?.map((assetClass) => assetClass.type?.toUpperCase())
                          .filter(Boolean)
                          .join(" | ") || "FUTURES"}
                      </Typography>
                      <Typography
                        variant="body1"
                        style={{
                          color: "#ffffff",
                          fontSize: "14px",
                          margin: 0,
                        }}
                      >
                        {algo.parameters?.symbols
                          ?.map((symbol) => symbol.symbol)
                          .join(" | ") || "NQ | ES | DOW"}
                      </Typography>
                    </div>

                    {/* Bottom Section - Statistics */}
                    <div className="d-flex justify-content-between">
                      <div>
                        <Typography
                          variant="caption"
                          style={{
                            color: "#888888",
                            fontSize: "11px",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          Trading Capital
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{
                            color: "#ffffff",
                            fontWeight: "bold",
                            fontSize: "14px",
                            margin: 0,
                          }}
                        >
                          $
                          {algo.parameters?.symbols
                            ?.reduce(
                              (total, symbol) =>
                                total + (symbol.maxCapital || 0),
                              0
                            )
                            .toLocaleString() || "0"}
                        </Typography>
                      </div>
                      <div>
                        <Typography
                          variant="caption"
                          style={{
                            color: "#888888",
                            fontSize: "11px",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          Total Returns
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{
                            color:
                              Number(algo.returns || 0) >= 0
                                ? "#4caf50"
                                : "#f44336",
                            fontWeight: "bold",
                            fontSize: "14px",
                            margin: 0,
                          }}
                        >
                          {Number(algo.returns || 0).toFixed(2)}%
                        </Typography>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default InstanceDetails;
